import {AbstractComponent} from './abstract-component';
import mitt, {Emitter} from 'mitt';

// Type for registry events
type RegistryEvent = {
  add: [string, AbstractComponent<any>],
  remove: string,
};

/**
 * Registry for any component. Helps in getting existing instances and reusing components across the site. Components
 * should add themselves to the registry at the end of their constructor.
 *
 * Using the various methods in the registry, you can access components or even wait until they have been instantiated
 * and use them in some other place again:
 *
 * ```ts
 * class ComponentA extends AbstractComponent {
 *   constructor() {
 *     super();
 *     // ...
 *     ComponentRegistry.add('component-a', this);
 *   }
 * }
 *
 * class ComponentB extends AbstractComponent {
 *   constructor() {
 *     super();
 *
 *     // Component B requires functionality of Component A
 *     ComponentRegistry.wait<ComponentA>('component-a')
 *       .then(componentA => {
 *         console.log('Component A has been added to the registry', componentA);
 *       });
 *
 *     ComponentRegistry.add('component-b', this);
 *   }
 * }
 *
 * ```
 *
 * @author Philipp Seiler <sep@earlybird.at>#
 * @see AbstractComponent
 */
export class ComponentRegistry {
  public static emitter: Emitter<RegistryEvent> = mitt<RegistryEvent>();

  // Contains all registered components
  private static readonly components: Map<string, AbstractComponent<any>> = new Map;

  /**
   * Adds a component to the registry.
   *
   * @param {string} id Unique ID to identify the component
   * @param {AbstractComponent} component Component to add
   * @param {boolean} [makeIdUnique=true] If true and the ID was already present in the registry, then the ID will
   * receive a counter to make it unique again
   * @returns {string} Final ID that was used to add the component to the registry
   */
  public static add(id: string, component: AbstractComponent<any>, makeIdUnique: boolean = true): string {
    id = ComponentRegistry.createUniqueId(id);
    ComponentRegistry.components.set(id, component);
    ComponentRegistry.emitter.emit('add', [id, component]);
    return id;
  }

  /**
   * Removes a component from the registry by its unique ID.
   *
   * @param {string} id ID of the component to delete
   */
  public static remove(id: string): void {
    if (ComponentRegistry.components.has(id)) {
      ComponentRegistry.components.delete(id);
      ComponentRegistry.emitter.emit('remove', id);
    }
  }

  /**
   * Removes a component from the registry by comparing registered instances.
   *
   * @param {AbstractComponent} instance Instance of a component to remove
   */
  public static removeByInstance(instance: AbstractComponent<any>): void {
    for (const id in ComponentRegistry.components) {
      const component = ComponentRegistry.components.get(id);
      if (component === instance) {
        ComponentRegistry.components.delete(id);
        ComponentRegistry.emitter.emit('remove', id);
      }
    }
  }

  /**
   * Gets all components.
   *
   * @returns {Map<string, AbstractComponent>}
   */
  public static getAll(): Map<string, AbstractComponent<any>> {
    return ComponentRegistry.components;
  }

  /**
   * Gets a component from the registry by its unique ID.
   *
   * @param {string} id ID of the component to get
   * @returns {AbstractComponent|null} Instance of the component, if existing
   */
  public static get<T extends AbstractComponent<any>>(id: string): T | null {
    if (ComponentRegistry.components.has(id)) {
      return ComponentRegistry.components.get(id) as T;
    }
    return null;
  }

  /**
   * Waits for a component with a specific ID to get added to the registry.
   *
   * @param {string} idToWaitFor ID of the component to wait for
   * @returns {Promise<AbstractComponent|null>} Instance of the component, if it will be added at some point
   */
  public static wait<T extends AbstractComponent<any>>(idToWaitFor: string): Promise<T> {
    return new Promise<T>(resolve => {
      // Component already present, resolve promise directly
      const existingComponent = ComponentRegistry.get(idToWaitFor);
      if (existingComponent !== null) {
        resolve(existingComponent as T);
        return;
      }

      // Component not yet available, wait for it to be added
      ComponentRegistry.emitter.on('add', ([id, component]) => {
        if (id === idToWaitFor) {
          resolve(component as T);
        }
      });
    });
  }

  /**
   * Creates a unique ID for a component to be added to the registry.
   *
   * @param {string} id ID to make unique
   * @param {number} [counter] Recursive counter to create a unique ID
   * @returns {string} Unique ID
   */
  private static createUniqueId(id: string, counter: number = 1): string {
    if (!ComponentRegistry.components.has(id)) {
      return id;
    }
    const newId = `${id}-${counter}`;
    if (!ComponentRegistry.components.has(newId)) {
      return newId;
    }
    return ComponentRegistry.createUniqueId(id, ++counter);
  }
}
